// Plan de séance, dans la fiche d'un entraînement (D-019).
//  - staff : composer le plan (bibliothèque du club, exercice nouveau, modèle de
//    séance du club, reprise d'un plan précédent), horaires calculés, alternative,
//    notes privées, publication aux joueuses, lancement du chrono de séance ;
//    enregistrer le plan comme modèle (D-040) ;
//  - joueuse / parent : « Au programme », si le coach l'a publié.
import { BIB_LABEL, isBib, type Bib } from '../domain/groups';
import { bibDot } from './exo-groups';
import {
  clubExercises, createExercise, currentUserId, myExerciseLibrary, deleteSessionTemplate, editorUrl, exerciseMedia, publishedPlan, recentPlans,
  saveSessionPlan, saveSessionTemplate, sessionPlan, sessionTemplateUsed, sessionTemplates,
  type ClubEvent, type Exercise, type SessionPlan, type SessionTemplate, type Team,
} from '../lib/api';
import { CATEGORY_LABEL, move, swapAlt, timeline, totalMinutes, type Category, type PlanItem } from '../domain/session';
import { $, $$, confirmTwice, html, mount, openSheet, toast } from './dom';
import { shortDate, time } from './format';
import { openLive } from './live';
import { mediaBlock } from './playbook';
import { drillById, exerciseLinks, setExerciseDrill } from '../lib/drills-api';
import { drillSummary } from '../domain/drills';
import { pickDrill } from './drills';
import { launchDrill } from './drill-run';
import { drillThumb, modePill } from './drill-view';
import { ARCHIVED_LABEL } from '../domain/library';

const CATS = Object.keys(CATEGORY_LABEL) as Category[];
const uid = () => crypto.randomUUID().slice(0, 8);

export async function renderPlanBlock(el: HTMLElement, ev: ClubEvent, date: string, start: Date, end: Date | null, team: Team | null, staff: boolean): Promise<void> {
  if (!staff) {
    const items = await publishedPlan(ev.id, date).catch(() => []);
    if (!items.length) { mount(el, html``); return; }
    const t = timeline(items.map((i, k) => ({ id: String(k), title: i.title, minutes: i.minutes ?? 0, category: i.category, description: i.description })), start);
    const mine = (k: number) => items[k]?.my_group ?? null;
    mount(el, html`
      <section class="block">
        <h3>Au programme</h3>
        <ol class="plan plan-ro">${t.map(s => html`
          <li><span class="plan-t">${time(s.start)}</span>
            <span class="plan-main"><strong>${s.item.title}</strong>${s.item.description ? html`<span class="muted small">${s.item.description}</span>` : ''}
              ${mine(Number(s.item.id)) ? html`<span class="gx-mine">${bibDot(isBib(mine(Number(s.item.id))!.bib) ? mine(Number(s.item.id))!.bib as Bib : null)}<strong>${mine(Number(s.item.id))!.name}</strong>${isBib(mine(Number(s.item.id))!.bib) ? ` · chasuble ${BIB_LABEL[mine(Number(s.item.id))!.bib as Bib]}` : ''}${mine(Number(s.item.id))!.mates.length ? ` · avec ${mine(Number(s.item.id))!.mates.join(', ')}` : ''}</span>`
                : (items[Number(s.item.id)]?.groups ?? 0) > 1 ? html`<span class="muted small">En ${items[Number(s.item.id)]!.groups} groupes</span>` : ''}</span>
            <span class="plan-min">${s.item.minutes} min</span></li>`)}
        </ol>
      </section>`);
    return;
  }

  let plan: SessionPlan = (await sessionPlan(ev.id, date)) ?? { event_id: ev.id, occurrence_date: date, items: [], coach_notes: null, published: false };
  const slotMin = end ? Math.round((end.getTime() - start.getTime()) / 60000) : null;

  const save = async (patch: Partial<SessionPlan>) => {
    plan = { ...plan, ...patch };
    try { await saveSessionPlan(plan); } catch { toast('Enregistrement impossible.', 'ko'); }
    draw();
  };

  const draw = () => {
    const total = totalMinutes(plan.items);
    const t = timeline(plan.items, start);
    mount(el, html`
      <section class="block">
        <div class="block-head">
          <h3>Plan de séance</h3>
          <span class="gauge ${slotMin && total > slotMin ? 'gauge-over' : ''}" style="--p:${slotMin ? Math.min(100, Math.round(100 * total / slotMin)) : 0}">${total}${slotMin ? ` / ${slotMin}` : ''} min</span>
        </div>
        ${plan.items.length ? html`<ol class="plan">${t.map((s, i) => html`
          <li data-i="${i}">
            <span class="plan-t">${time(s.start)}</span>
            <button type="button" class="plan-main" data-edit="${i}">
              <strong>${s.item.title}</strong>
              <span class="muted small">${CATEGORY_LABEL[s.item.category] ?? ''}${s.item.alt ? html` · plan B : ${s.item.alt.title}` : ''}</span>
            </button>
            <span class="plan-min">${s.item.minutes} min</span>
            <span class="plan-act">
              ${s.item.alt ? html`<button type="button" data-swap="${i}" aria-label="Passer au plan B" title="Passer au plan B">⇄</button>` : ''}
              <button type="button" data-up="${i}" aria-label="Monter" ${i === 0 ? 'disabled' : ''}>▲</button>
              <button type="button" data-down="${i}" aria-label="Descendre" ${i === plan.items.length - 1 ? 'disabled' : ''}>▼</button>
            </span>
          </li>`)}
          <li class="plan-end"><span class="plan-t">${time(t[t.length - 1]!.end)}</span><span class="muted small">fin${end && t[t.length - 1]!.end > end ? ' — dépasse le créneau' : ''}</span></li>
        </ol>` : html`<p class="muted">Pas encore de plan. Compose-le avec la bibliothèque du club, ou reprends celui d'une séance précédente.</p>`}
        <div class="btn-row">
          <button type="button" class="btn btn-ghost btn-sm" id="b-add-ex">+ Exercice</button>
          <button type="button" class="btn btn-ghost btn-sm" id="b-tpl">Partir d'un modèle</button>
          <button type="button" class="btn btn-ghost btn-sm" id="b-reuse">Reprendre un plan</button>
          ${plan.items.length ? html`<button type="button" class="btn btn-ghost btn-sm" id="b-save-tpl">Enregistrer comme séance type</button>` : ''}
          ${plan.items.length ? html`<button type="button" class="btn btn-primary btn-sm" id="b-live">▶ Lancer la séance</button>` : ''}
        </div>
        ${plan.items.length ? html`
          <label class="check"><input type="checkbox" id="c-pub" ${plan.published ? 'checked' : ''}>
            <span><strong>Visible de l’équipe</strong><br><span class="muted">Elle voit le programme (sans le plan B ni tes notes).</span></span></label>` : ''}
        <label class="field"><span>Notes du coach (privées)</span>
          <textarea id="t-notes" rows="2" maxlength="4000" placeholder="Objectifs, points d'attention…">${plan.coach_notes ?? ''}</textarea></label>
      </section>`);

    $(el, '#b-add-ex')!.addEventListener('click', () => openAddSheet(team, item => save({ items: [...plan.items, item] })));
    $(el, '#b-reuse')!.addEventListener('click', () => team && openReuseSheet(team, ev, date, items => save({ items })));
    $(el, '#b-tpl')!.addEventListener('click', () => team && openTemplatesSheet(team, plan.items.length > 0, (items, mode, notes) =>
      save({ items: mode === 'append' ? [...plan.items, ...items] : items, coach_notes: plan.coach_notes || notes })));
    $(el, '#b-save-tpl')?.addEventListener('click', () => team && openSaveTemplateSheet(team, ev, plan));
    $(el, '#b-live')?.addEventListener('click', () => openLive(plan.items, ev.title, undefined, undefined, team));
    $<HTMLInputElement>(el, '#c-pub')?.addEventListener('change', e => save({ published: (e.target as HTMLInputElement).checked }));
    $<HTMLTextAreaElement>(el, '#t-notes')!.addEventListener('change', e => save({ coach_notes: (e.target as HTMLTextAreaElement).value.trim() || null }));
    for (const b of $$<HTMLButtonElement>(el, '[data-up]')) b.addEventListener('click', () => { const i = Number(b.dataset.up); save({ items: move(plan.items, i, i - 1) }); });
    for (const b of $$<HTMLButtonElement>(el, '[data-down]')) b.addEventListener('click', () => { const i = Number(b.dataset.down); save({ items: move(plan.items, i, i + 1) }); });
    for (const b of $$<HTMLButtonElement>(el, '[data-swap]')) b.addEventListener('click', () => {
      const i = Number(b.dataset.swap);
      save({ items: plan.items.map((x, k) => (k === i ? swapAlt(x) : x)) });
      toast('Plan B activé');
    });
    for (const b of $$<HTMLButtonElement>(el, '[data-edit]')) b.addEventListener('click', () => {
      const i = Number(b.dataset.edit);
      openItemSheet(plan.items[i]!, {
        onSave: item => save({ items: plan.items.map((x, k) => (k === i ? item : x)) }),
        onRemove: () => save({ items: plan.items.filter((_, k) => k !== i) }),
      }, { team });
    });
  };
  draw();
}

const itemForm = (it: Partial<PlanItem>, withLibrary: boolean) => html`
  <form class="form" id="f-item" novalidate>
    <label class="field"><span>Exercice</span><input name="title" value="${it.title ?? ''}" maxlength="120" required placeholder="ex. 3 contre 2 en continu"></label>
    <div class="row-2">
      <label class="field"><span>Durée (min)</span><input name="minutes" type="number" inputmode="numeric" min="1" max="180" value="${it.minutes ?? 10}"></label>
      <label class="field"><span>Catégorie</span><select name="category">${CATS.map(c => html`<option value="${c}" ${c === (it.category ?? 'technique') ? 'selected' : ''}>${CATEGORY_LABEL[c]}</option>`)}</select></label>
    </div>
    ${withLibrary ? '' : html`<div id="it-drill"></div>`}
    <label class="field"><span>Consignes (facultatif)</span><textarea name="description" rows="3" maxlength="4000">${it.description ?? ''}</textarea></label>
    <details class="alt-box" ${it.alt ? 'open' : ''}>
      <summary>Plan B (si l'effectif ou le gymnase ne suit pas)</summary>
      <div class="row-2">
        <label class="field"><span>Alternative</span><input name="alt_title" value="${it.alt?.title ?? ''}" maxlength="120"></label>
        <label class="field"><span>Durée (min)</span><input name="alt_minutes" type="number" min="1" max="180" value="${it.alt?.minutes ?? ''}"></label>
      </div>
    </details>
    ${withLibrary ? html`<label class="check"><input type="checkbox" name="to_library" checked><span><strong>Ajouter à la bibliothèque du club</strong><br><span class="muted">Tous les coachs pourront le réutiliser.</span></span></label>` : ''}
    <p class="form-msg" role="alert"></p>
    <button class="btn btn-primary" type="submit">Enregistrer</button>
    ${!withLibrary ? html`<button type="button" class="btn btn-danger-ghost" id="b-rm">Retirer du plan</button>` : ''}
  </form>`;

function readItem(f: HTMLFormElement, base: Partial<PlanItem>): PlanItem | null {
  const d = new FormData(f);
  const title = String(d.get('title') ?? '').trim();
  if (!title) return null;
  const altTitle = String(d.get('alt_title') ?? '').trim();
  const category = String(d.get('category')) as Category;
  return {
    // Ce que le formulaire ne montre pas (groupes, play, drill, thème…) reste sur l'élément.
    ...base,
    id: base.id ?? uid(), title, minutes: Math.max(1, Number(d.get('minutes')) || 10), category,
    description: String(d.get('description') ?? '').trim() || null, exercise_id: base.exercise_id ?? null,
    alt: altTitle ? { title: altTitle, minutes: Math.max(1, Number(d.get('alt_minutes')) || Number(d.get('minutes')) || 10), category } : null,
  };
}

export async function openItemSheet(item: PlanItem, h: { onSave: (i: PlanItem) => void; onRemove: () => void }, opts: { team?: Team | null } = {}) {
  // Un exercice de la bibliothèque : son schéma (éditeur des plays), ses vidéos et images.
  const ex = item.exercise_id ? await exerciseMedia(item.exercise_id).catch(() => null) : null;
  const form = item.exercise_id
    ? html`${ex?.archived_at ? html`<p class="sup-use">Exercice ${ARCHIVED_LABEL} : cette séance le garde tel quel.</p>` : ''}
      ${ex ? mediaBlock(ex.videos ?? [], ex.images ?? []) : ''}
      <a class="btn btn-ghost" href="${editorUrl({ exo: item.exercise_id })}">${ex?.diagram ? 'Voir / modifier le schéma' : 'Dessiner le schéma'}</a>
      ${itemForm(item, false)}`
    : itemForm(item, false);
  let cur = item;
  openSheet('Exercice', form, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#f-item')!;
    if (opts.team) void drillField(sheet, f, opts.team, () => cur, next => { cur = next; h.onSave(cur); });
    f.addEventListener('submit', e => {
      e.preventDefault();
      const it = readItem(f, cur);
      if (!it) { $(f, '.form-msg')!.textContent = 'Donne un nom à l\'exercice.'; return; }
      close(); h.onSave(it);
    });
    confirmTwice($<HTMLButtonElement>(sheet, '#b-rm')!, 'Confirmer : retirer cet exercice', async () => { close(); h.onRemove(); });
  });
}

/** « Drill de l'exercice » (D-098, maquette 14) : ▶ Essayer, Changer, Retirer. Le lien est porté par l'exercice de la
 *  bibliothèque quand on peut le modifier (le ▶ le suit dans toutes les séances), sinon par l'élément de cette séance. */
async function drillField(sheet: HTMLElement, f: HTMLFormElement, team: Team, get: () => PlanItem, set: (i: PlanItem) => void): Promise<void> {
  const host = $(sheet, '#it-drill');
  if (!host) return;
  const paint = async () => {
    const cur = get();
    const ex = cur.exercise_id ? (await exerciseLinks([cur.exercise_id]).catch(() => []))[0] ?? null : null;
    const fromEx = cur.drill_id === undefined;
    const id = fromEx ? ex?.drill_id ?? null : cur.drill_id ?? null;
    const d = id ? await drillById(id, true).catch(() => null) : null;   // même supprimé (D-178) : la séance le garde
    mount(host, html`<div class="dr-field"><span>Drill de l’exercice</span>
      ${d ? html`<div class="dr-dlink">${drillThumb(d)}<span class="t"><strong>${d.title}</strong><small>${modePill(d.kind)} · ${drillSummary(d.kind, d.config)}${d.archived_at ? html` · <span class="sup-gone">${ARCHIVED_LABEL}</span>` : ''}</small></span>
        <div class="btn-row"><button type="button" class="btn btn-primary btn-sm" data-dtry>▶ Essayer</button><button type="button" class="btn btn-ghost btn-sm" data-dchange>Changer</button><button type="button" class="btn btn-link btn-sm" data-drm>Retirer</button></div></div>`
        : html`<button type="button" class="btn btn-ghost btn-sm" data-dchange style="justify-self:start">+ Associer un drill</button>`}
      <small>${d ? (fromEx && ex ? 'Enregistré avec l’exercice de la bibliothèque : le bouton ▶ le suit dans chaque séance et dans la séance en direct.' : 'Pour cette séance : le bouton ▶ apparaît dans la séance et dans la séance en direct.')
        : 'Réaction, circuit ou fractionné : un ▶ pour le lancer pendant l’exercice.'}</small></div>`);
    $(host, '[data-dtry]')?.addEventListener('click', () => { if (d) void launchDrill(d, { team, staff: true, trial: true }); });
    $(host, '[data-dchange]')!.addEventListener('click', () => void pickDrill({
      clubId: team.club_id, title: 'Le drill de l’exercice', current: id,
      create: { exerciseId: cur.exercise_id ?? null, hint: 'Tu reviens ici une fois le drill enregistré.' },
      onPick: async picked => {
        const base = readItem(f, get()) ?? get();
        const viaEx = base.exercise_id ? await setExerciseDrill(base.exercise_id, picked.id).catch(() => false) : false;
        const next = { ...base };
        if (viaEx) delete next.drill_id; else next.drill_id = picked.id;
        set(next);
        toast(viaEx ? 'Drill associé à l’exercice : le ▶ le suit dans chaque séance' : 'Drill associé à cette séance');
        void paint();
      },
    }));
    $(host, '[data-drm]')?.addEventListener('click', async () => {
      const base = readItem(f, get()) ?? get();
      const viaEx = fromEx && base.exercise_id ? await setExerciseDrill(base.exercise_id, null).catch(() => false) : false;
      const next = { ...base };
      if (viaEx) delete next.drill_id; else next.drill_id = null;
      set(next);
      toast(viaEx ? 'Drill retiré de l’exercice' : 'Plus de drill pour cette séance');
      void paint();
    });
  };
  await paint();
}

export async function openAddSheet(team: Team | null, onAdd: (i: PlanItem) => void) {
  const all: Exercise[] = team ? await clubExercises(team.club_id).catch(() => []) : [];
  const [me, mineIds] = await Promise.all([currentUserId(), myExerciseLibrary().catch(() => new Set<string>())]);
  const mine = all.filter(x => x.created_by === me || mineIds.has(x.id)), others = all.filter(x => !mine.includes(x));
  const lib = [...mine, ...others];
  openSheet('Ajouter un exercice', html`
    <div class="seg" role="tablist">
      <button type="button" data-tab="lib" aria-selected="${String(lib.length > 0)}">Bibliothèque (${mine.length})</button>
      <button type="button" data-tab="new" aria-selected="${String(lib.length === 0)}">Nouveau</button>
    </div>
    <div class="screen" data-pane="lib" ${lib.length ? '' : 'hidden'}>
      <input class="search" type="search" placeholder="Chercher un exercice" aria-label="Chercher">
      ${mine.length ? '' : html`<p class="muted small">Ta bibliothèque est vide : voici ceux du club.</p>`}
      <ul class="pick-list">${lib.map((x, k) => html`${k === mine.length && mine.length && others.length ? html`<li class="pick-sep">Ceux du club</li>` : ''}<li><button type="button" data-ex="${x.id}" data-q="${`${x.title} ${CATEGORY_LABEL[x.category]}`.toLowerCase()}">
        <span class="pick-main"><strong>${x.title}</strong><span class="muted small">${CATEGORY_LABEL[x.category]}${x.minutes ? ` · ${x.minutes} min` : ''}</span></span></button></li>`)}</ul>
    </div>
    <div data-pane="new" ${lib.length ? 'hidden' : ''}>${itemForm({}, !!team)}</div>`, (sheet, close) => {
    for (const b of $$<HTMLButtonElement>(sheet, '[data-tab]')) b.addEventListener('click', () => {
      $$(sheet, '[data-tab]').forEach(x => x.setAttribute('aria-selected', String(x === b)));
      $$(sheet, '[data-pane]').forEach(p => { p.hidden = p.dataset.pane !== b.dataset.tab; });
    });
    const search = $<HTMLInputElement>(sheet, '.search');
    search?.addEventListener('input', () => {
      const q = search.value.trim().toLowerCase();
      $$(sheet, '[data-ex]').forEach(b => { b.parentElement!.hidden = !!q && !b.dataset.q!.includes(q); });
    });
    for (const b of $$<HTMLButtonElement>(sheet, '[data-ex]')) b.addEventListener('click', () => {
      const x = lib.find(e => e.id === b.dataset.ex)!;
      close();
      onAdd({ id: uid(), title: x.title, minutes: x.minutes ?? 10, category: x.category, description: x.description, exercise_id: x.id });
    });
    const f = $<HTMLFormElement>(sheet, '#f-item')!;
    f.addEventListener('submit', async e => {
      e.preventDefault();
      const it = readItem(f, {});
      if (!it) { $(f, '.form-msg')!.textContent = 'Donne un nom à l\'exercice.'; return; }
      if (team && new FormData(f).get('to_library') === 'on') {
        try {
          const created = await createExercise({ club_id: team.club_id, title: it.title, category: it.category, minutes: it.minutes, description: it.description ?? null });
          it.exercise_id = created.id;
        } catch { toast('Ajouté au plan, mais pas à la bibliothèque.', 'ko'); }
      }
      close(); onAdd(it);
    });
  });
}

export async function openReuseSheet(team: Team, ev: ClubEvent, date: string, onPick: (items: PlanItem[]) => void) {
  const plans = (await recentPlans(team.id).catch(() => [])).filter(p => !(p.event_id === ev.id && p.occurrence_date === date) && p.items.length);
  openSheet('Reprendre un plan', html`
    ${plans.length ? html`<ul class="pick-list">${plans.map((p, i) => html`<li><button type="button" data-plan="${i}">
      <span class="pick-main"><strong>${shortDate(p.occurrence_date)} · ${p.events.title}</strong>
        <span class="muted small">${p.items.length} exercice${p.items.length > 1 ? 's' : ''} · ${totalMinutes(p.items)} min · ${p.items.slice(0, 3).map(x => x.title).join(', ')}${p.items.length > 3 ? '…' : ''}</span></span></button></li>`)}</ul>
      <p class="hint">Le plan est copié : tu peux ensuite l'ajuster sans toucher à l'original.</p>`
      : html`<p class="muted">Aucun plan précédent pour cette équipe.</p>`}`, (sheet, close) => {
    for (const b of $$<HTMLButtonElement>(sheet, '[data-plan]')) b.addEventListener('click', () => {
      const p = plans[Number(b.dataset.plan)]!;
      close();
      onPick(p.items.map(x => ({ ...x, id: uid() })));
      toast('Plan repris');
    });
  });
}

/** Modèles de séance du club (D-040) : partir d'un modèle, en remplaçant le plan ou à la suite. */
export async function openTemplatesSheet(team: Team, hasPlan: boolean, onPick: (items: PlanItem[], mode: 'replace' | 'append', notes: string | null, theme: string | null) => void) {
  const [list, me] = await Promise.all([sessionTemplates(team.club_id).catch(() => [] as SessionTemplate[]), currentUserId()]);
  const levels = [...new Set(list.map(t => t.level).filter((l): l is string => !!l))].sort();
  let mode: 'replace' | 'append' = 'replace';
  openSheet('Séances types', html`
    ${list.length ? html`
      ${hasPlan ? html`<div class="seg" role="radiogroup" aria-label="Le plan actuel">
        <button type="button" role="radio" data-mode="replace" aria-pressed="true">Remplacer le plan</button>
        <button type="button" role="radio" data-mode="append" aria-pressed="false">Ajouter à la suite</button></div>` : ''}
      <input class="search" type="search" placeholder="Chercher un modèle, un thème" aria-label="Chercher un modèle">
      ${levels.length > 1 ? html`<div class="chip-toggles" role="group" aria-label="Public">${levels.map(l => html`<button type="button" class="chip-toggle" data-level="${l}" aria-pressed="false">${l}</button>`)}</div>` : ''}
      <ul class="pick-list tpl-list">${list.map(t => html`
        <li data-q="${`${t.title} ${t.theme ?? ''} ${t.level ?? ''} ${t.items.map(i => i.title).join(' ')}`.toLowerCase()}" data-lv="${t.level ?? ''}">
          <button type="button" data-tpl="${t.id}">
            <span class="pick-main"><strong>${t.title}</strong>
              <span class="muted small">${[t.level, `${t.items.length} exercice${t.items.length > 1 ? 's' : ''}`, `${totalMinutes(t.items)} min`].filter(Boolean).join(' · ')}</span>
              ${t.theme ? html`<span class="small">${t.theme}</span>` : ''}
              <span class="muted small">${t.items.slice(0, 4).map(i => i.title).join(' → ')}${t.items.length > 4 ? '…' : ''}</span></span>
          </button>
          ${t.created_by === me ? html`<button type="button" class="link-x" data-del-tpl="${t.id}" aria-label="Supprimer le modèle ${t.title}">supprimer</button>` : ''}
        </li>`)}</ul>
      <p class="hint">Le modèle est copié dans la séance : tu l'ajustes ensuite sans toucher au modèle.</p>`
    : html`<p class="muted">Aucune séance type pour l'instant. Compose une séance, puis « Enregistrer comme séance type » : tous les coachs du club la retrouveront ici.</p>`}`, (sheet, close) => {
    for (const b of $$<HTMLButtonElement>(sheet, '[data-mode]')) b.addEventListener('click', () => {
      mode = b.dataset.mode as 'replace' | 'append';
      $$(sheet, '[data-mode]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
    });
    const search = $<HTMLInputElement>(sheet, '.search');
    const filter = () => {
      const q = (search?.value ?? '').trim().toLowerCase();
      const on = $$<HTMLButtonElement>(sheet, '[data-level][aria-pressed="true"]').map(b => b.dataset.level!);
      for (const li of $$<HTMLLIElement>(sheet, '.tpl-list > li')) li.hidden = (!!q && !li.dataset.q!.includes(q)) || (on.length > 0 && !on.includes(li.dataset.lv!));
    };
    search?.addEventListener('input', filter);
    for (const b of $$<HTMLButtonElement>(sheet, '[data-level]')) b.addEventListener('click', () => {
      b.setAttribute('aria-pressed', String(b.getAttribute('aria-pressed') !== 'true')); filter();
    });
    for (const b of $$<HTMLButtonElement>(sheet, '[data-tpl]')) b.addEventListener('click', () => {
      const t = list.find(x => x.id === b.dataset.tpl)!;
      close();
      void sessionTemplateUsed(t.id);
      onPick(t.items.map(x => ({ ...x, id: uid() })), mode, t.coach_notes, t.theme);
      toast(`Modèle « ${t.title} » ${mode === 'append' ? 'ajouté à la suite' : 'repris'}`);
    });
    for (const b of $$<HTMLButtonElement>(sheet, '[data-del-tpl]')) {
      confirmTwice(b, 'Confirmer : supprimer le modèle', async () => {
        try { await deleteSessionTemplate(b.dataset.delTpl!); b.closest('li')!.remove(); toast('Modèle supprimé'); }
        catch { toast('Seul son auteur ou le bureau peut le supprimer.', 'ko'); }
      });
    }
  });
}

/** Enregistrer le plan comme modèle : titre, objectif, public visé ; tous les coachs du club le retrouvent. */
export function openSaveTemplateSheet(team: Team, ev: ClubEvent, plan: SessionPlan) {
  openSheet('Enregistrer comme séance type', html`
    <form class="form" id="f-tpl" novalidate>
      <label class="field"><span>Nom du modèle</span><input name="title" maxlength="120" required placeholder="ex. Contre-attaque et repli" value=""></label>
      <label class="field"><span>Objectif (facultatif)</span><input name="theme" maxlength="200" placeholder="ex. Courir après chaque récupération"></label>
      <label class="field"><span>Pour qui (facultatif)</span><input name="level" maxlength="40" value="${team.category ?? ''}" placeholder="ex. U13, Seniors"></label>
      <label class="check"><input type="checkbox" name="notes" ${plan.coach_notes ? '' : 'disabled'}><span><strong>Garder mes notes</strong><br><span class="muted">${plan.coach_notes ? 'Visibles des autres coachs du club.' : 'Pas de notes sur cette séance.'}</span></span></label>
      <p class="muted small">${plan.items.length} exercice${plan.items.length > 1 ? 's' : ''} · ${totalMinutes(plan.items)} min · plans B compris. Tous les coachs du club pourront partir de ce modèle.</p>
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">Enregistrer le modèle</button>
    </form>`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#f-tpl')!;
    $<HTMLInputElement>(f, '[name=title]')!.value = ev.title && ev.title !== 'Entraînement' ? ev.title : '';
    f.addEventListener('submit', async e => {
      e.preventDefault();
      const d = new FormData(f);
      const title = String(d.get('title') ?? '').trim();
      if (!title) { $(f, '.form-msg')!.textContent = 'Donne un nom au modèle.'; return; }
      try {
        await saveSessionTemplate({
          club_id: team.club_id, title, theme: String(d.get('theme') ?? '').trim() || null, level: String(d.get('level') ?? '').trim() || null,
          items: plan.items, coach_notes: d.get('notes') === 'on' ? plan.coach_notes : null,
        });
        close(); toast('Modèle enregistré : tous les coachs du club peuvent le réutiliser');
      } catch { $(f, '.form-msg')!.textContent = 'Enregistrement impossible. Réessaie.'; }
    });
  });
}
